import { useState, useEffect } from "react";
import { useSetIsVideoPaused } from "../VideoContextProvider/context/state/isPaused.js";
import { useSetIsReady } from "../VideoContextProvider/context/state/isReady.js";
import { useSetSubtitles, useSetSubtitlesEnabled } from "../VideoContextProvider/context/state/subtitles.js";
import { useSetSubtitleTrack } from "../VideoContextProvider/context/state/subtitleTrack.js";
import { useSetVideoVolume } from "../VideoContextProvider/context/state/volume.js";
import { useVideoController } from "../VideoContextProvider/context/videoController.js";
import { useSetVideoTime } from "../VideoContextProvider/context/state/time.js";
import { useSetAdBreakList } from "../VideoContextProvider/context/state/adBreakList.js";
import { useSetCurrentAdBreak } from "../VideoContextProvider/context/state/currentAdBreak.js";
import { registerVideoControllerHandlers } from "./registerVideoControllerHandlers.js";
import { useSetIsAdPlaying } from "../VideoContextProvider/context/state/isAdPlaying.js";
import { useEvent } from "../../lib/hooks/useEvent.js";
import { useErrorReporting, getGlobalContext } from "../VideoContextProvider/context/state/reporting.js";
import { debug } from "../../log.js";
import { useSetIsVideoBuffering } from "../VideoContextProvider/context/state/isBuffering.js";
import { handleOnStateChanged } from "./sessionEventHandlers.js";
import { SessionState } from "@sky-uk-ott/core-video-sdk-js";
const debugSession = debug.extend("cvsdk-session");
function useCvsdkSession({ latestSessionConfig, onVideoControllerReady, playerController }) {
  const [errorCount, setErrorCount] = useState(0);
  const onReady = useEvent((v) => onVideoControllerReady == null ? void 0 : onVideoControllerReady(v));
  const videoController = useVideoController();
  const setIsVideoPaused = useSetIsVideoPaused();
  const setIsVideoBuffering = useSetIsVideoBuffering();
  const setSubtitleTrack = useSetSubtitleTrack();
  const setVolume = useSetVideoVolume();
  const setSubtitles = useSetSubtitles();
  const setSubtitlesEnabled = useSetSubtitlesEnabled();
  const setIsReady = useSetIsReady();
  const setIsAdPlaying = useSetIsAdPlaying();
  const setAdBreakList = useSetAdBreakList();
  const setCurrentAdBreak = useSetCurrentAdBreak();
  const setVideoTime = useSetVideoTime();
  const { reportError } = useErrorReporting();
  useEffect(() => {
    if (!playerController) {
      return;
    }
    let sessionAbandoned = false;
    let currentAdBreakEndTime = void 0;
    playerController.onSessionCreated((session) => {
      if (sessionAbandoned) {
        session.stop();
        return;
      }
      setIsReady(false);
      setIsAdPlaying(false);
      setAdBreakList([]);
      setCurrentAdBreak(void 0);
      registerVideoControllerHandlers(session, videoController);
      function setReady() {
        setIsReady((isReady) => {
          if (!isReady) {
            setTimeout(() => {
              if (!sessionAbandoned) {
                debugSession("Session is ready");
                onReady(videoController);
              }
            }, 500);
          }
          return true;
        });
      }
      session.onStateChanged((state) => {
        if ([SessionState.Playing, SessionState.Paused].includes(state)) {
          setReady();
        }
      });
      session.onMuteChanged((muted) => setVolume((prev) => ({ ...prev, muted })));
      session.onVolumeChanged((volume) => setVolume((prev) => ({ ...prev, volume })));
      session.onAdBreakDataReceived((adBreakData) => {
        const adBreakDuration = adBreakData.map((adBreak) => (adBreak == null ? void 0 : adBreak.expectedDuration) || 0).reduce((currentTotalDuration, adDuration) => {
          return currentTotalDuration + adDuration;
        }, 0);
        const currentTime = /* @__PURE__ */new Date();
        const expectedEndTime = new Date(currentTime.getTime() + adBreakDuration * 1e3);
        currentAdBreakEndTime = expectedEndTime;
        setAdBreakList(adBreakData);
      });
      session.onAdPositionChanged((adPosition) => {
        if (adPosition == null ? void 0 : adPosition.ad) {
          setCurrentAdBreak(adPosition);
        }
      });
      session.onAdBreakStarted(() => {
        setIsAdPlaying(true);
      });
      session.onAdBreakFinished(() => {
        setIsAdPlaying(false);
        currentAdBreakEndTime = void 0;
        setCurrentAdBreak(void 0);
      });
      session.onAvailableSubtitlesTracksChanged((tracks) => {
        var _a;
        const id = (_a = tracks[0]) == null ? void 0 : _a.id;
        if (id) {
          setSubtitleTrack(id);
        }
      });
      session.onSubtitleCuesChanged((subtitleCues) => {
        var _a;
        setSubtitles(((_a = subtitleCues[0]) == null ? void 0 : _a.text) ?? "");
        if (subtitleCues.length > 0) {
          setSubtitlesEnabled(true);
        }
      });
      session.onStateChanged((state) => {
        handleOnStateChanged(
          state,
          debugSession,
          SessionState,
          setIsReady,
          setIsVideoPaused,
          setIsVideoBuffering
        );
      });
      session.onPlaybackTimelineUpdated((time) => {
        var _a, _b, _c;
        setVideoTime({
          isAtLiveEdge: (((_a = time.seekableRange) == null ? void 0 : _a.end) || 0) - time.position <= 3 || false,
          position: time.position || 0,
          seekableRange: {
            end: ((_b = time.seekableRange) == null ? void 0 : _b.end) || 0,
            start: ((_c = time.seekableRange) == null ? void 0 : _c.start) || 0
          },
          seekToLiveEdge: session.seekToLiveEdge
        });
        if (currentAdBreakEndTime && currentAdBreakEndTime < /* @__PURE__ */new Date()) {
          setIsAdPlaying(false);
        }
      });
      session.onError((err) => {
        if (err.code === "VSF:SDK.LOADING_LIMIT.UNKNOWN_REASON.MAIN_CONTENT") {
          debugSession("Restarting session due to loading limit");
          setErrorCount(errorCount + 1);
        } else {
          const error = new Error(err.message);
          error.cause = err;
          debugSession("error:", err);
          reportError({
            error,
            severity: "fatal",
            context: {
              ...getGlobalContext()
            }
          });
        }
      });
    });
    playerController.createSession(latestSessionConfig);
    return () => {
      playerController.destroy();
      sessionAbandoned = true;
    };
  }, [
  // The following are stable references and aren't expected to change
  onReady,
  reportError,
  setIsReady,
  setVolume,
  setSubtitleTrack,
  setSubtitles,
  setSubtitlesEnabled,
  setIsVideoPaused,
  setIsVideoBuffering,
  setIsAdPlaying,
  setCurrentAdBreak,
  setAdBreakList,
  setVideoTime,
  errorCount,
  // End stable references
  // If any of these changes, the session will be restarted.
  playerController,
  latestSessionConfig,
  videoController]
  );
}
export {
  useCvsdkSession };
//# sourceMappingURL=dist/components/CoreVideoPlayer/useCvsdkSession.js.map